// Sharing → Settings (contracts/index-cards.md §1, §4, §5): the index.* and share.* config keys. Whether free
// players see their Logic Index (index.free), the number of index holders below which players' standing and the
// curve are hidden (index.minPlayers), and the App Store Connect provider token for campaign links on the web's
// landing pages (share.appStoreCampaign). Config writes are admin (PATCH /config, all or nothing, audited);
// everyone else reads.

import { useState } from "react";
import { Link } from "react-router";
import { useConfig } from "../../api/queries";
import type { JsonValue } from "../../api/schemas";
import { useSaveSharingConfig, useSharing } from "../../api/sharing";
import { useAuth, useCan } from "../../auth/AuthContext";
import { Button, Card, Chip, ConfirmDialog, ErrorState, Field, Loading, PageHeader, TextInput } from "../../components/ui";
import { addDays, todayUtc } from "../../lib/dates";
import { fmtInt } from "../../lib/format";
import { campaignLink, MIN_PLAYERS, PROVIDER_TOKEN_RE, readSharingConfig, type SharingConfig, sharingConfigProblems } from "../../lib/sharing";
import l from "../learn/learn.module.css";
import { SwitchRow } from "../messaging/components";
import pages from "../pages.module.css";
import { DEFAULT_DAYS } from "./components";
import s from "./sharing.module.css";

export function SharingSettingsPage() {
  const config = useConfig();
  const canEdit = useCan("config.edit");
  const { admin } = useAuth();
  return (
    <>
      <PageHeader
        title="Sharing settings"
        meta={
          canEdit ? (
            <span>
              The index.* and share.* config keys. <Link to="/sharing">← Sharing</Link>
            </span>
          ) : (
            <span className={pages.readOnly} data-testid="read-only">
              Read-only · settings need the admin role (you are {admin?.role})
            </span>
          )
        }
      />
      {config.isPending ? (
        <Card className={s.settings}>
          <Loading rows={6} />
        </Card>
      ) : config.isError ? (
        <ErrorState error={config.error} onRetry={() => void config.refetch()} />
      ) : (
        <SettingsCard key={JSON.stringify(readSharingConfig(config.data.values))} saved={readSharingConfig(config.data.values)} canEdit={canEdit} />
      )}
    </>
  );
}

function SettingsCard({ saved, canEdit }: { saved: SharingConfig; canEdit: boolean }) {
  const set = useSaveSharingConfig();
  const today = todayUtc();
  // The population, as the Sharing page loads it (same range, same cache).
  const population = useSharing({ from: addDays(today, -(DEFAULT_DAYS - 1)), to: today }).data?.population;
  const [free, setFree] = useState(saved.free);
  const [minPlayers, setMinPlayers] = useState(String(saved.minPlayers));
  const [token, setToken] = useState(saved.campaign.providerToken ?? "");
  const [confirming, setConfirming] = useState(false);

  const problems = sharingConfigProblems({ minPlayers, providerToken: token });
  const min = Number(minPlayers);
  const nextToken = token.trim() || null;
  const writes: { key: string; label: string; from: string; to: string; value: JsonValue }[] = [];
  if (free !== saved.free) writes.push({ key: "index.free", label: "Free players see their index", from: saved.free ? "on" : "off", to: free ? "on" : "off", value: free });
  if (min !== saved.minPlayers) writes.push({ key: "index.minPlayers", label: "Standing shown from", from: fmtInt(saved.minPlayers), to: fmtInt(min), value: min });
  if (nextToken !== saved.campaign.providerToken)
    writes.push({ key: "share.appStoreCampaign", label: "App Store provider token", from: saved.campaign.providerToken ?? "none", to: nextToken ?? "none", value: { providerToken: nextToken } });

  const save = () => set.mutate(Object.fromEntries(writes.map((w) => [w.key, w.value])), { onSuccess: () => setConfirming(false) });
  const hidden = population && Number.isInteger(min) && population.holders < min;

  return (
    <Card className={s.settings} title="Logic Index and links" actions={<Chip tone={saved.free ? "success" : "muted"}>{saved.free ? "free for everyone" : "Pro only"}</Chip>}>
      <fieldset className={l.fieldset} disabled={!canEdit}>
        <div className={s.body} data-testid="sharing-settings">
          <SwitchRow
            title="Free players see their Logic Index"
            hint="index.free. On: free players see their index, tier, standing, strongest skill and the curve, and can share them; Pro keeps the history charts. Off: the index is Pro only, as before."
            checked={free}
            onChange={setFree}
            label="Free players see their Logic Index"
            testId="index-free"
          />
          <Field
            label="Standing shown from (index holders)"
            hint={
              <>
                index.minPlayers, {MIN_PLAYERS.min}–{fmtInt(MIN_PLAYERS.max)}. Below this many holders, players don't see "Sharper than X%" or the curve, and the
                cards show the tier line instead.
                {population ? (
                  <span data-testid="holders-now">
                    {" "}
                    Now: {fmtInt(population.holders)} holders, so the standing is {hidden ? "hidden" : "shown"}.
                  </span>
                ) : null}
              </>
            }
          >
            <TextInput type="number" min={MIN_PLAYERS.min} max={MIN_PLAYERS.max} step={1} value={minPlayers} onChange={(ev) => setMinPlayers(ev.target.value)} style={{ width: 140 }} data-testid="min-players" />
          </Field>
          <Field
            label="App Store provider token"
            hint={
              <>
                share.appStoreCampaign.providerToken: digits from App Store Connect → App Analytics → Campaigns. Set, the web's store links carry{" "}
                <code>pt</code> and <code>ct</code> (e.g. index-story), and installs show per campaign. Empty: plain store links.
              </>
            }
          >
            <TextInput
              inputMode="numeric"
              autoComplete="off"
              placeholder="e.g. 118425963"
              value={token}
              onChange={(ev) => setToken(ev.target.value.replace(/\s/g, ""))}
              style={{ width: 220 }}
              data-testid="provider-token"
            />
          </Field>
          <span className={s.link} data-testid="campaign-link">
            {campaignLink(PROVIDER_TOKEN_RE.test(token.trim()) ? token.trim() : null)}
          </span>
          {problems.length ? (
            <ul className={s.problems} data-testid="settings-problems">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          ) : null}
          {canEdit ? (
            <div className={s.inline}>
              <Button variant="primary" disabled={!writes.length || problems.length > 0} onClick={() => setConfirming(true)} data-testid="settings-save">
                Save…
              </Button>
              {writes.length ? (
                <span className={pages.faint}>
                  {writes.length} unsaved {writes.length === 1 ? "change" : "changes"}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </fieldset>
      <ConfirmDialog open={confirming} title="Save sharing settings?" confirmLabel="Save" busy={set.isPending} error={set.error} onCancel={() => setConfirming(false)} onConfirm={save}>
        <ul className={s.changes} data-testid="settings-changes">
          {writes.map((w) => (
            <li key={w.key}>
              <span>{w.label}</span>
              <span className={s.mono}>
                {w.from} → {w.to}
              </span>
            </li>
          ))}
        </ul>
        <p className={pages.footNote}>Apps and the web pick these up on their next config fetch (GET /v1/config).</p>
      </ConfirmDialog>
    </Card>
  );
}
